import { useState, useCallback } from 'react';
import type { PendingOrder } from '../../types/checkout';
import { getCSRFToken } from '../../utils/csrf';
import { route } from '../../utils/route';

interface PendingOrdersGateProps {
  pendingOrders: PendingOrder[];
  onContinue: (remainingOrders: PendingOrder[]) => void;
}

export function PendingOrdersGate({ pendingOrders, onContinue }: PendingOrdersGateProps) {
  const [orders, setOrders] = useState(pendingOrders);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cancelAndContinue = useCallback(async (order: PendingOrder) => {
    setCancellingId(order.id);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('id', String(order.id));

      const response = await fetch(route('api.orders.cancel'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-XSRF-TOKEN': getCSRFToken(),
          'Accept': 'text/html,application/xhtml+xml',
        },
        body: formData,
      });

      if (response.ok || response.redirected) {
        const remaining = orders.filter(o => o.id !== order.id);
        onContinue(remaining);
      } else {
        setError('No se pudo cancelar el pedido. Intentá de nuevo.');
        setCancellingId(null);
      }
    } catch {
      setError('Error de conexión. Intentá de nuevo.');
      setCancellingId(null);
    }
  }, [orders, onContinue]);

  const isBusy = cancellingId !== null;

  // Single order — streamlined view
  if (orders.length === 1) {
    const order = orders[0];
    const addressSuffix = order.address_display ? ` para ${order.address_display}` : '';

    return (
      <div className="mx-auto max-w-[600px] px-4 pb-10 pt-6">
        <div className="py-4 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-500">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="m-0 text-2xl font-bold text-gray-800">
            Hace {order.created_at_human.replace('hace ', '')} creaste otro pedido{addressSuffix}
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-gray-500">
            Si continuás, este pedido se va a agregar además del anterior.
          </p>
        </div>

        {/* Order card */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold text-gray-800">
                Pedido #{order.id}
              </p>
              <p className="mt-0.5 text-sm text-gray-500">
                ${order.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </p>
              <p className="mt-1 text-xs text-gray-400">{order.items_summary}</p>
            </div>
            <span className="mt-0.5 flex-shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
              Pendiente
            </span>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {order.is_user_cancellable ? (
            <button
              type="button"
              onClick={() => cancelAndContinue(order)}
              disabled={isBusy}
              className="w-full rounded-xl border-2 border-red-400 bg-white px-4 py-3.5 text-[15px] font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {cancellingId === order.id ? 'Cancelando...' : 'Cancelar el pedido anterior y continuar'}
            </button>
          ) : (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm text-amber-700">
              El pedido anterior ya está en preparación y no puede cancelarse.
            </p>
          )}
          <button
            type="button"
            onClick={() => onContinue(orders)}
            disabled={isBusy}
            className="w-full rounded-xl bg-green-500 px-4 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Mantener ambos pedidos y continuar
          </button>
        </div>
      </div>
    );
  }

  // Multiple orders
  return (
    <div className="mx-auto max-w-[600px] px-4 pb-10 pt-6">
      <div className="py-4 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-500">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h1 className="m-0 text-2xl font-bold text-gray-800">
          Tenés {orders.length} pedidos pendientes
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-gray-500">
          Si continuás, el nuevo pedido se va a agregar además de los anteriores. Podés cancelar los que no necesites.
        </p>
      </div>

      <div className="space-y-3">
        {orders.map(order => (
          <div key={order.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-gray-800">
                  Pedido #{order.id}
                  {order.address_display && (
                    <span className="font-normal text-gray-400"> &middot; {order.address_display}</span>
                  )}
                </p>
                <p className="mt-0.5 text-sm text-gray-500">
                  {order.created_at_human} &middot; ${order.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </p>
                <p className="mt-1 text-xs text-gray-400">{order.items_summary}</p>
              </div>
              <span className="mt-0.5 flex-shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
                Pendiente
              </span>
            </div>

            {error && cancellingId === order.id && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}

            {order.is_user_cancellable ? (
              <button
                type="button"
                onClick={() => cancelAndContinue(order)}
                disabled={isBusy}
                className="mt-3 rounded-lg border border-red-300 bg-white px-3.5 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {cancellingId === order.id ? 'Cancelando...' : 'Cancelar y continuar'}
              </button>
            ) : (
              <p className="mt-3 text-xs text-amber-700">
                Este pedido ya está en preparación y no puede cancelarse.
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6">
        <button
          type="button"
          onClick={() => onContinue(orders)}
          disabled={isBusy}
          className="w-full rounded-xl bg-green-500 px-4 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Mantener todos y hacer un pedido nuevo
        </button>
      </div>
    </div>
  );
}
